export function AsIsVsRemodelFAQ() {
  const faqs = [
    {
      question: 'Will I get less money if I sell my apartment building AS IS?',
      answer: 'Not necessarily. While the offer price may be lower than a fully renovated property, you avoid spending $500K-$2M+ on renovations and carrying costs. In most cases, owners net MORE money by selling AS IS because they avoid the massive costs and risks of renovation. Plus, you get cash immediately instead of waiting 12-18 months.'
    },
    {
      question: 'How much less will I get for selling AS IS versus after renovating?',
      answer: 'The price difference depends on your property and market, but typically 10-20% less than a renovated property. However, when you factor in renovation costs ($500K-$2M+), carrying costs ($10K-$50K/month), and time value of money, selling AS IS usually nets you MORE profit. Our case studies show owners often lose $500K-$1M+ trying to renovate first.'
    },
    {
      question: 'What condition does my apartment building need to be in to sell AS IS?',
      answer: 'Any condition! We buy apartment buildings with deferred maintenance, structural issues, outdated units, problem tenants, code violations, and everything in between. The worse the condition, the more sense it makes to sell AS IS rather than trying to fix everything yourself.'
    },
    {
      question: 'Can I sell AS IS if my building has tenants?',
      answer: 'Absolutely! In fact, having tenants in place is often preferred because it shows the property generates income. We handle all tenant communications and lease transfers. You don\'t need to relocate anyone or deal with vacancy issues.'
    },
    {
      question: 'How long does it take to sell an apartment building AS IS?',
      answer: 'With us, you can close in as little as 7-30 days. Compare that to 12-18 months if you renovate first, plus another 3-6 months to market and sell. Selling AS IS gets you cash in hand up to 18 months faster.'
    },
    {
      question: 'Do I need to make any repairs before selling AS IS?',
      answer: 'No! That\'s the whole point of selling AS IS. You don\'t fix anything—no repairs, no painting, no cleaning, nothing. We buy the property in its current condition and handle all improvements after closing.'
    },
    {
      question: 'What if I already started renovations but ran out of money?',
      answer: 'This is actually a common situation we handle. We can buy your property mid-renovation, saving you from the stress of finishing the project. You avoid throwing more money into a project that may not pay off, and we take over from wherever you stopped.'
    },
    {
      question: 'Will selling AS IS affect my taxes differently than selling after renovation?',
      answer: 'Selling AS IS can actually be more tax-efficient because you avoid depreciation recapture on renovation costs and have lower capital gains. Consult with a tax professional, but many owners find the tax implications of selling AS IS are more favorable.'
    },
    {
      question: 'How do you determine the AS IS value of my apartment building?',
      answer: 'We analyze your property\'s current income, expenses, location, condition, and market cap rates. We use the income approach that all commercial real estate investors use, giving you a fair offer based on the property\'s actual cash flow potential, not arbitrary renovation costs.'
    },
    {
      question: 'What if my apartment building needs major repairs like a new roof or HVAC?',
      answer: 'Perfect! These are exactly the types of expensive repairs that make selling AS IS the smart choice. We factor these needs into our offer and handle all major repairs after closing. You avoid the $50K-$150K+ expense and the headache of managing major construction.'
    },
    {
      question: 'Can I get multiple offers if I sell AS IS?',
      answer: 'Yes, but be careful. Some buyers lowball AS IS properties hoping you\'re desperate. We provide fair, competitive offers based on true market value. You\'re welcome to get multiple offers, but make sure you\'re comparing apples to apples—some buyers have hidden fees or contingencies.'
    },
    {
      question: 'Is there a minimum or maximum size apartment building you buy AS IS?',
      answer: 'We buy apartment buildings of all sizes, from small 4-unit properties to large 100+ unit complexes. The AS IS approach works for any size multifamily property. Larger buildings actually benefit even more from selling AS IS due to the massive renovation costs involved.'
    },
    {
      question: 'What happens to my tenants when I sell AS IS?',
      answer: 'Existing leases transfer to the new owner (us). Tenants stay in place and continue paying rent. We handle all communications and ensure a smooth transition. Your tenants often don\'t even notice a change except for where they send rent payments.'
    },
    {
      question: 'Do I need to provide financial records to sell AS IS?',
      answer: 'Yes, we\'ll need basic financial information like rent rolls, operating expenses, and tax returns. This helps us make a fair offer based on the property\'s actual income. Don\'t worry if your records aren\'t perfect—we work with what you have.'
    },
    {
      question: 'What if I owe more on my mortgage than the AS IS value?',
      answer: 'This can be challenging, but we have experience with short sales and can sometimes negotiate with lenders. Contact us to discuss your specific situation—we may have creative solutions that can help you avoid foreclosure and move on.'
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-5xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Frequently Asked Questions
          </h2>
          <p className="text-xl text-gray-700">
            Get answers to the most common questions about selling your apartment building AS IS versus remodeling first.
          </p>
        </div>

        {/* FAQ Items */}
        <div className="space-y-6 mb-16">
          {faqs.map((faq, index) => (
            <div key={index} className="bg-white rounded-2xl p-8 shadow-lg border border-gray-200">
              <h3 className="text-xl font-bold mb-4 flex items-start gap-4">
                <i className="ri-question-line text-2xl text-teal-600 flex-shrink-0 mt-1"></i>
                <span>{faq.question}</span>
              </h3>
              <div className="pl-12">
                <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Still Have Questions */}
        <div className="bg-gradient-to-br from-teal-50 to-white rounded-2xl p-10 text-center border-2 border-teal-200">
          <h3 className="text-3xl font-bold mb-4">Still Have Questions?</h3>
          <p className="text-xl text-gray-700 mb-8">
            Every apartment building and situation is unique. Contact us for personalized answers about your specific property.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="tel:1234567890"
              className="px-8 py-4 bg-teal-600 text-white rounded-xl font-bold text-lg hover:bg-teal-700 transition-colors flex items-center gap-3 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line text-2xl"></i>
              Call Us Now
            </a>
            <button
              onClick={() => {
                const form = document.getElementById('contact-form');
                if (form) {
                  form.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
              }}
              className="px-8 py-4 bg-white text-teal-600 rounded-xl font-bold text-lg hover:bg-gray-50 transition-colors border-2 border-teal-600 flex items-center gap-3 whitespace-nowrap"
            >
              <i className="ri-mail-line text-2xl"></i>
              Get Your Offer
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}